import { useEffect, useMemo, useState } from 'react';
import { canEditChildProfile } from '../../../constants/roles';
import { getChildCareTeam } from '../../../services/childAccessService';
import { getE2EMockData, isE2EMockEnabled } from '../../../services/e2eMock';
import { getChildProfileSetupStatus } from '../../../utils/profileCompletion';

const useMobileChildProfileState = ({
  activeChild,
  children,
  getUserRoleForChild,
}) => {
  const [careTeamsByChildId, setCareTeamsByChildId] = useState({});

  useEffect(() => {
    if (isE2EMockEnabled()) {
      const mockCareTeams = getE2EMockData().careTeamsByChildId || {};
      setCareTeamsByChildId(mockCareTeams);
      return undefined;
    }

    let isMounted = true;

    if (!children.length) {
      setCareTeamsByChildId({});
      return undefined;
    }

    const loadCareTeams = async () => {
      const entries = await Promise.all(children.map(async (child) => {
        try {
          const members = await getChildCareTeam(child.id);
          return [child.id, Array.isArray(members) ? members : []];
        } catch (error) {
          return [child.id, []];
        }
      }));

      if (isMounted) {
        setCareTeamsByChildId(Object.fromEntries(entries));
      }
    };

    loadCareTeams();

    return () => {
      isMounted = false;
    };
  }, [children]);

  const activeChildCareTeam = useMemo(
    () => careTeamsByChildId[activeChild?.id] || [],
    [activeChild?.id, careTeamsByChildId]
  );

  const hasLoadedActiveChildCareTeam = Boolean(
    activeChild?.id
    && Object.prototype.hasOwnProperty.call(careTeamsByChildId, activeChild.id)
  );

  const activeChildProfileSetupStatus = useMemo(
    () => getChildProfileSetupStatus(
      activeChild || {},
      hasLoadedActiveChildCareTeam ? { careTeamMembers: activeChildCareTeam } : {}
    ),
    [activeChild, activeChildCareTeam, hasLoadedActiveChildCareTeam]
  );

  const dashboardProfileSetupStatus = useMemo(() => {
    const summary = activeChildProfileSetupStatus.completionSummary;
    if (!summary?.total) {
      return activeChildProfileSetupStatus;
    }

    const sectionLabel = summary.total === 1 ? 'section' : 'sections';

    return {
      ...activeChildProfileSetupStatus,
      completionLabel: `${summary.complete} of ${summary.total} ${sectionLabel} complete`,
    };
  }, [activeChildProfileSetupStatus]);

  const activeChildRole = getUserRoleForChild?.(activeChild?.id) || null;

  return {
    activeChildCareTeam,
    activeChildProfileSetupStatus,
    activeChildRole,
    canEditActiveChildProfile: canEditChildProfile(activeChildRole),
    careTeamsByChildId,
    dashboardProfileSetupStatus,
    profileProgress: activeChildProfileSetupStatus.completionPercent,
  };
};

export default useMobileChildProfileState;
